import { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { Mail, Check, X, Clock, RefreshCcw, Search, AlertTriangle } from "lucide-react";

const STATUS_STYLE = {
  sent:           { bg:"#dcfce7", color:"#15803d", border:"1px solid #86efac",  label:"Sent"       },
  failed:         { bg:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5",  label:"Failed"     },
  skipped_no_key: { bg:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0",  label:"No API Key" },
  queued:         { bg:"#fef3c7", color:"#92400e", border:"1px solid #fde68a",  label:"Queued"     },
};
const STATUS_ICON = { sent:Check, failed:X, skipped_no_key:AlertTriangle, queued:Clock };

export default function AdminEmailLog() {
  const [items,   setItems]   = useState([]);
  const [stats,   setStats]   = useState({});
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");
  const [q,       setQ]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/email-log?limit=200${filter!=="all"?`&status=${filter}`:""}`);
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? items.filter(i=>[i.to,i.subject,i.reason].some(v=>v?.toLowerCase().includes(q.toLowerCase())))
    : items;

  return (
    <div className="space-y-5" data-testid="admin-email-log-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Notifications</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Email Log</h1>
          <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>All outgoing emails — sent, failed, and skipped records</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 h-9 rounded-xl text-[12px] font-semibold transition-colors"
          style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
          <RefreshCcw className={`h-3.5 w-3.5 ${loading?"animate-spin":""}`}/> Refresh
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { key:"all",           label:"Total",      value:stats.total   ||0, accent:"#1e5bb8" },
          { key:"sent",          label:"Sent",       value:stats.sent    ||0, accent:"#16a34a" },
          { key:"failed",        label:"Failed",     value:stats.failed  ||0, accent:"#e31e24" },
          { key:"skipped_no_key",label:"No API Key", value:stats.skipped ||0, accent:"#94a3b8" },
        ].map(({ key, label, value, accent }) => (
          <button key={key} onClick={() => setFilter(key)}
            className="bg-white rounded-2xl p-4 text-left shadow-sm transition-all"
            style={{
              border:"1px solid #dde3ef",
              borderTop:`3px solid ${accent}`,
              boxShadow: filter===key ? `0 0 0 2px ${accent}33` : undefined,
            }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{label}</div>
            <div className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>{value}</div>
          </button>
        ))}
      </div>

      {/* No API key warning */}
      {(stats.skipped||0) > 0 && !stats.sent && (
        <div className="flex items-start gap-3 p-4 rounded-2xl"
          style={{ background:"#fffbeb", border:"1px solid #fde68a" }}>
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#d97706" }}/>
          <div className="text-[13px]" style={{ color:"#78350f" }}>
            <strong>Resend API key not set.</strong> All emails are logged but not delivered.
            Add <code className="font-mono text-[11px] px-1 py-0.5 rounded-md" style={{ background:"rgba(0,0,0,0.08)" }}>RESEND_API_KEY</code> to your{" "}
            <code className="font-mono text-[11px] px-1 py-0.5 rounded-md" style={{ background:"rgba(0,0,0,0.08)" }}>.env</code> and rebuild.
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color:"#94a3b8" }}/>
        <input value={q} onChange={e=>setQ(e.target.value)}
          placeholder="Search recipient, subject, reason…"
          className="w-full pl-9 h-10 pr-3 rounded-xl text-[13px] outline-none"
          style={{ background:"#ffffff", border:"1px solid #dde3ef", color:"#0f172a" }}/>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Status","To","Subject","Reason","Time"].map(h=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase text-left"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => {
              const st  = STATUS_STYLE[e.status] || STATUS_STYLE.queued;
              const Icon = STATUS_ICON[e.status] || Clock;
              return (
                <tr key={e.id||i} className="hover:bg-[#f5f7fb] transition-colors"
                  style={{ borderBottom: i < filtered.length-1 ? "1px solid #f0f4f8" : "none" }}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={{ background:st.bg, color:st.color, border:st.border }}>
                      <Icon className="h-3 w-3"/> {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[13px] max-w-[180px] truncate" style={{ color:"#0f172a" }}>{e.to}</td>
                  <td className="px-4 py-3 text-[13px] max-w-[220px] truncate" style={{ color:"#0f172a" }}>{e.subject}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                      {e.reason || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono whitespace-nowrap" style={{ color:"#64748b" }}>
                    {e.created_at ? new Date(e.created_at).toLocaleString("en-AU") : "—"}
                  </td>
                </tr>
              );
            })}
            {filtered.length===0 && !loading && (
              <tr><td colSpan={5} className="py-14 text-center">
                <Mail className="h-10 w-10 mx-auto mb-3" style={{ color:"#c5cfe3" }}/>
                <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                  {q ? "No emails match" : "No emails logged yet"}
                </div>
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="py-8 text-center text-[12px] font-mono uppercase tracking-widest animate-pulse" style={{ color:"#94a3b8" }}>
                Loading…
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="px-4 py-2 text-right text-[11px] font-mono" style={{ borderTop:"1px solid #f1f5f9", color:"#94a3b8" }}>
          {filtered.length} records
        </div>
      </div>
    </div>
  );
}